import * as React from "react";
import { useLanguage } from "./Language";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Button } from "@mui/material";
import { BranchLimit, useBranchLimits } from "./BranchLimits";

export interface LocationSummaryProps {
  branchLimits: BranchLimit[];
  /**
   * if set, BranchLimit.selected is ignored
   */
  selected: readonly BranchLimit["identifier"][];
  prefixText?: string;
}
export const LocationSummary: React.FC<LocationSummaryProps> = ({
  branchLimits,
  selected,
  prefixText,
}) => {
  const { translate } = useLanguage();
  const selectedBranches = (branchLimits || [])
    .filter((branch) => !branch.hidden && branch.displayInBranchLimits)
    .filter((branch) => selected.includes(branch.identifier));

  return (
    <React.Fragment>
      {selectedBranches.length === 0
        ? `${prefixText ? `${prefixText} ` : ""}${translate("allLocations")}`
        : selectedBranches.length === 1
        ? `${prefixText ? `${prefixText} ` : ""}${
            selectedBranches[0].displayName
          }`
        : `${prefixText ? `${prefixText} ` : ""}${
            selectedBranches.length
          } ${translate("selectedLocations")}`}
    </React.Fragment>
  );
};

export interface LocationDialogProps {
  open: boolean;
  onSubmit: (value: readonly string[]) => void;
  onClose?: () => void;
}
export const LocationDialog: React.FC<LocationDialogProps> = ({
  open,
  onSubmit,
  onClose,
}) => {
  const theme = useTheme();
  const { selectedBranchIds, branchLimits } = useBranchLimits();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  console.log("rendering Location Dialog", open);
  console.log(branchLimits);
  const branches = branchLimits.filter(
    (branch) => !branch.hidden && branch.displayInBranchLimits
  );

  // checked state is detatched from selected
  const [checked, setChecked] = React.useState<readonly string[]>([
    ...selectedBranchIds,
  ]);
  React.useEffect(() => setChecked(selectedBranchIds), [selectedBranchIds]);

  const [isOpen, setIsOpen] = React.useState<boolean>(open);

  React.useEffect(() => {
    setIsOpen(open);
  }, [open]);
  console.log("isOpen", isOpen);
  const { translate } = useLanguage();
  const handleClose = () => {
    setIsOpen(false);
    setChecked(selectedBranchIds); // reset
    if (onClose) {
      onClose();
    }
  };

  const handleSubmit = () => {
    setIsOpen(false);
    onSubmit(checked);
  };

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleToggleAll = () => {
    if (checked.length === branches.length) {
      setChecked([]);
    } else {
      setChecked(branches.map(({ identifier }) => identifier));
    }
  };

  return (
    <Dialog onClose={handleClose} open={isOpen} fullScreen={fullScreen}>
      <DialogTitle>{translate("selectLocations")}</DialogTitle>

      <List
        sx={{
          overflow: "auto",
          width: "100%",
          maxWidth: "700px",
          minWidth: "320px",
        }}
        disablePadding
      >
        <ListSubheader disableGutters>
          <ListItemButton onClick={handleToggleAll} role="listitem">
            <ListItemIcon>
              <Checkbox
                checked={checked.length === branches.length}
                indeterminate={
                  checked.length !== 0 && checked.length !== branches.length
                }
                tabIndex={-1}
                disableRipple
                slotProps={{
                  input: {
                    "aria-labelledby": "location-check-all-label",
                  },
                }}
              />
            </ListItemIcon>
            <ListItemText
              id="location-check-all-label"
              primary={translate("selectedLocations")}
            />
          </ListItemButton>
          <Divider />
        </ListSubheader>
        {branches.map((value) => {
          const labelId = `location-${value.identifier}-label`;

          return (
            <ListItemButton
              key={value.identifier}
              onClick={handleToggle(value.identifier)}
              role="listitem"
            >
              <ListItemIcon>
                <Checkbox
                  checked={checked.indexOf(value.identifier) !== -1}
                  tabIndex={-1}
                  disableRipple
                  slotProps={{
                    input: {
                      "aria-labelledby": labelId,
                    },
                  }}
                />
              </ListItemIcon>
              <ListItemText id={labelId} primary={value.displayName} />
            </ListItemButton>
          );
        })}
      </List>
      <DialogActions>
        <Button onClick={handleClose}>{translate("cancel")}</Button>
        <Button onClick={handleSubmit} autoFocus>
          <LocationSummary
            prefixText={translate("search")}
            branchLimits={branchLimits}
            selected={checked}
          ></LocationSummary>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
